// frontend/src/components/LandingPage/StatsSection.jsx
import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
    TrendingDown, 
    Clock, 
    Shield,
    Zap,
    BarChart3,
    Grid3x3
} from 'lucide-react';

// Hook para animação de contador
const useCountUp = (end, duration = 2000, start = 0) => {
    const [count, setCount] = useState(start);
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    useEffect(() => {
        if (!isInView) return;

        let startTime = null;
        const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / duration, 1);
            
            // Easing function (ease-out)
            const easeOut = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(start + (end - start) * easeOut));

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                setCount(end);
            }
        };

        requestAnimationFrame(animate);
    }, [isInView, end, duration, start]);

    return [count, ref];
};

// Componente separado para cada card de estatística (permite usar hooks corretamente)
const StatCard = ({ stat, index }) => {
    const [count, ref] = useCountUp(stat.value);
    const Icon = stat.icon;

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            className="group relative bg-white rounded-xl md:rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:-translate-y-2"
        >
            {/* Ícone */}
            <div className={`inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-lg md:rounded-xl ${stat.bgColor} mb-3 sm:mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <Icon className={`h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 ${stat.color}`} />
            </div>

            {/* Número Animado */}
            <div className="mb-2 sm:mb-3">
                <span className="text-2xl sm:text-3xl md:text-5xl font-black text-gray-900">
                    {typeof stat.value === 'number' && stat.value % 1 !== 0 
                        ? count.toFixed(1) 
                        : count}
                </span>
                <span className="text-xl sm:text-2xl md:text-3xl font-bold text-higeia-dark ml-1 sm:ml-2">
                    {stat.suffix}
                </span>
            </div>

            {/* Label */}
            <p className="text-xs sm:text-sm md:text-base text-gray-600 font-medium leading-relaxed">
                {stat.label}
            </p>

            {/* Efeito de brilho no hover */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-higeia-light/0 to-higeia-light/0 group-hover:from-higeia-light/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />
        </motion.div>
    );
};

const stats = [
    {
        icon: TrendingDown,
        value: 40,
        suffix: '%',
        label: 'Redução no tempo de documentação',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50'
    },
    {
        icon: Clock,
        value: 15,
        suffix: 'h',
        label: 'Economia de horas por semana',
        color: 'text-green-600',
        bgColor: 'bg-green-50'
    },
    {
        icon: Shield,
        value: 99.9,
        suffix: '%',
        label: 'Uptime garantido',
        color: 'text-orange-600',
        bgColor: 'bg-orange-50'
    },
    {
        icon: Zap,
        value: 3,
        suffix: 'x',
        label: 'Mais rápido que métodos tradicionais',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50'
    },
    {
        icon: BarChart3,
        value: 85,
        suffix: '%',
        label: 'Aumento na satisfação dos pacientes',
        color: 'text-teal-600',
        bgColor: 'bg-teal-50'
    },
    {
        icon: Grid3x3,
        value: 20,
        suffix: '+',
        label: 'Aplicações integradas no sistema',
        color: 'text-purple-600',
        bgColor: 'bg-purple-50'
    }
];

const StatsSection = () => {
    return (
        <section className="py-20 sm:py-24 bg-gradient-to-b from-white to-gray-50">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                {/* Cabeçalho */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
                        Resultados que{' '}
                        <span className="bg-gradient-to-r from-higeia-dark to-higeia-medium bg-clip-text text-transparent">
                            falam por si
                        </span>
                    </h2>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        Números reais de organizações que transformaram sua gestão com o Higeia.tech
                    </p>
                </motion.div>

                {/* Grid de Estatísticas */}
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                    {stats.map((stat, index) => (
                        <StatCard key={index} stat={stat} index={index} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default StatsSection;

